import React from 'react';

type Series = { name: string; color: string; data: number[] };

interface LineChartProps {
  labels: string[]; // x-axis labels (e.g., mm-yyyy)
  series: Series[]; // multiple series (e.g., novos, perdidos)
  width?: number; // px
  height?: number; // px
}

const LineChart: React.FC<LineChartProps> = ({ labels, series, width = 720, height = 220 }) => {
  const padding = { top: 16, right: 16, bottom: 30, left: 30 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const maxY = Math.max(
    1,
    ...series.flatMap(s => s.data).map(v => (Number.isFinite(v) ? v : 0)),
  );

  const pointsFor = (data: number[]) => {
    const n = Math.max(1, data.length);
    return data.map((v, i) => {
      const x = (i / (n - 1)) * chartW + padding.left;
      const y = padding.top + chartH - (Math.min(v, maxY) / maxY) * chartH;
      return `${x},${y}`;
    }).join(' ');
  };

  return (
    <svg width={width} height={height} className="home-chart-svg">
      {/* axes */}
      <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartH} stroke="#ddd" />
      <line x1={padding.left} y1={padding.top + chartH} x2={padding.left + chartW} y2={padding.top + chartH} stroke="#ddd" />

      {/* grid lines */}
      {[0.25, 0.5, 0.75].map((p) => (
        <line
          key={p}
          x1={padding.left}
          y1={padding.top + chartH * (1 - p)}
          x2={padding.left + chartW}
          y2={padding.top + chartH * (1 - p)}
          stroke="#f0f0f0"
        />
      ))}

      {/* series polylines */}
      {series.map((s, idx) => (
        <polyline
          key={idx}
          fill="none"
          stroke={s.color}
          strokeWidth={2}
          points={pointsFor(s.data)}
        />
      ))}

      {/* labels */}
      {labels.map((l, i) => {
        const x = (i / Math.max(1, labels.length - 1)) * chartW + padding.left;
        const y = padding.top + chartH + 18;
        return (
          <text key={i} x={x} y={y} textAnchor="middle" fontSize={10} fill="#666">
            {l}
          </text>
        );
      })}

      {/* max value label */}
      <text x={padding.left - 6} y={padding.top + 8} textAnchor="end" fontSize={10} fill="#888">
        {maxY}
      </text>
    </svg>
  );
};

export default LineChart;